/**
 * Career Identity Card™ — the share asset.
 *
 * Canon rules this page obeys:
 *  1. Matches, Career DNA™ signals, the assessed date and the report hash come
 *     from the SAME sealed Experience Engine payload `/report` renders
 *     (`consumeStudentReport` → `learner_reports`). The card and the report can
 *     therefore never disagree.
 *  2. Archetypes are the canonical eight (`ARCHETYPE_TAGLINES` in
 *     `src/features/assessment-v2/results.ts`) and are read from the learner's
 *     own saved v2 run — the only place an archetype tally exists. If there is
 *     no tally, the card renders WITHOUT a persona rather than inventing one.
 *  3. Salary bands come from the market layer (`career_market_intelligence`),
 *     keyed by the payload's `career_id`. A career with no price shows no price.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Sparkles,
  Download,
  MessageCircle,
  Linkedin,
  Twitter,
  Share2,
  Loader2,
  ImageIcon,
} from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import WeKitLogo from "@/components/WeKitLogo";
import OnetAttribution from "@/components/OnetAttribution";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { fetchMyReferralAssets } from "@/lib/referral";
import { consumeStudentReport } from "@/lib/reportPrefetch";
import type { StudentPayload } from "@/components/report/reportTypes";
import { pct } from "@/components/report/reportTypes";
import CareerCard, {
  type CardTheme,
  type CareerCardData,
} from "@/components/card/CareerCard";
import {
  ARCHETYPE_TAGLINES,
  composeHeadline,
  composeLearner,
  formatSalary,
  topArchetypes,
} from "@/features/assessment-v2/results";
import { emptyVector, type Archetype, type ArchetypeVector } from "@/features/assessment-v2/archetypes";
import { storageKey, type Band } from "@/features/assessment-v2/band";
import type { AssessmentV2State } from "@/features/assessment-v2/types";
import { cloudStorage } from "@/lib/cloudStore";

const THEMES: { key: CardTheme; label: string; swatch: string }[] = [
  { key: "apple", label: "Apple", swatch: "bg-[#f5f5f7] border border-[#e5e5e7]" },
  { key: "canva", label: "Canva", swatch: "bg-gradient-to-br from-[#FF5F1F] to-[#FFD166]" },
  { key: "glass", label: "Glass", swatch: "bg-gradient-to-br from-[#0818A8] to-[#00665B]" },
  { key: "dark", label: "Dark", swatch: "bg-[#0a0a0a]" },
];

/**
 * The canonical eight archetypes — no more, no fewer. The words are NOT
 * maintained here: taglines come from `ARCHETYPE_TAGLINES`, which is canon.
 * This map only carries the card's display title and emoji.
 */
const ARCHETYPE_PERSONA: Record<Archetype, { title: string; emoji: string }> = {
  Builder: { title: "The Builder", emoji: "🔧" },
  Thinker: { title: "The Thinker", emoji: "🧠" },
  Creator: { title: "The Creator", emoji: "🎨" },
  Connector: { title: "The Connector", emoji: "🤝" },
  Organizer: { title: "The Organizer", emoji: "📋" },
  Leader: { title: "The Leader", emoji: "👑" },
  Caregiver: { title: "The Caregiver", emoji: "💚" },
  Advocate: { title: "The Advocate", emoji: "📣" },
};

const BANDS: Band[] = ["18plus", "15to18", "12to14"];

/** The learner's most recently updated saved v2 run, across all bands. */
function loadLatestV2State(): AssessmentV2State | null {
  let best: AssessmentV2State | null = null;
  for (const band of BANDS) {
    try {
      const raw = cloudStorage.getItem(storageKey(band));
      if (!raw) continue;
      const parsed = JSON.parse(raw) as AssessmentV2State;
      if (!parsed?.part2) continue;
      if (!best || (parsed.updatedAt ?? "") > (best.updatedAt ?? "")) best = parsed;
    } catch { /* ignore a corrupt run */ }
  }
  return best;
}

type Archetypes = { primary: Archetype; secondary: Archetype | null } | null;

/**
 * Archetypes + headline from the learner's own saved run. Returns null when
 * there is no tally — the card then renders with no persona at all.
 */
function deriveArchetypeAndHeadline(): { archetypes: Archetypes; headline: string | null } {
  const state = loadLatestV2State();
  if (!state) return { archetypes: null, headline: null };

  const tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
  const scored = Object.values(tally).filter((v) => (v ?? 0) > 0).length;
  if (scored === 0) return { archetypes: null, headline: null };

  const { primary, secondary } = topArchetypes(tally);
  // Only claim a secondary streak when a second archetype actually scored.
  const hasSecondary = scored > 1 && (tally[secondary] ?? 0) > 0;

  let headline: string | null = null;
  try {
    headline = composeHeadline(composeLearner(state), tally);
  } catch { headline = null; }

  return {
    archetypes: { primary, secondary: hasSecondary ? secondary : null },
    headline,
  };
}

/** Salary bands for the payload's top matches, from the market layer only. */
async function fetchSalaryBands(ciIds: string[]): Promise<Record<string, string | null>> {
  const out: Record<string, string | null> = {};
  if (!ciIds.length) return out;
  const { data } = await supabase
    .from("career_market_intelligence")
    .select("ci_id,salary_lo_lpa,salary_hi_lpa")
    .in("ci_id", ciIds);
  for (const row of (data ?? []) as Array<{
    ci_id: string; salary_lo_lpa: number | null; salary_hi_lpa: number | null;
  }>) {
    const band = formatSalary(row.salary_lo_lpa, row.salary_hi_lpa);
    out[row.ci_id] = band === "No salary data yet" ? null : band;
  }
  return out;
}

function assessedOn(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function buildCard(
  payload: StudentPayload,
  archetypes: Archetypes,
  headline: string | null,
  salaries: Record<string, string | null>,
): CareerCardData {
  const persona = archetypes ? ARCHETYPE_PERSONA[archetypes.primary] : null;

  const matches = (payload.recommendations ?? [])
    .slice()
    .sort((a, b) => a.recommendation_rank - b.recommendation_rank)
    .slice(0, 3)
    .map((r) => ({ title: r.career_name, salary: salaries[r.career_id] ?? null }));

  const signals = (payload.career_dna?.top_intelligences ?? [])
    .filter((i) => !i.is_null && i.value != null)
    .slice(0, 2)
    .map((i) => ({ name: i.intelligence_name, value: pct(i.value) }));

  const date = assessedOn(payload.provenance?.generated_at);
  const hash = payload.report_hash ? payload.report_hash.slice(0, 8) : null;
  const credibility =
    date && hash ? `Assessed ${date} · report ${hash}`
      : date ? `Assessed ${date}`
      : hash ? `Report ${hash}` : null;

  return {
    personaTitle: persona?.title ?? null,
    personaEmoji: persona?.emoji ?? null,
    tagline: archetypes ? ARCHETYPE_TAGLINES[archetypes.primary] : null,
    secondaryLine: archetypes?.secondary
      ? `with a strong ${archetypes.secondary} streak`
      : null,
    headline,
    matches,
    signals,
    credibility,
  };
}

export default function CareerCardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<CardTheme>("glass");
  const [data, setData] = useState<CareerCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [handle, setHandle] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      try {
        const payload = (await consumeStudentReport(user.id)) as StudentPayload;
        if (!payload) { if (!cancelled) setLoading(false); return; }

        const { archetypes, headline } = deriveArchetypeAndHeadline();
        const ciIds = (payload.recommendations ?? [])
          .slice()
          .sort((a, b) => a.recommendation_rank - b.recommendation_rank)
          .slice(0, 3)
          .map((r) => r.career_id);
        const salaries = await fetchSalaryBands(ciIds);

        if (!cancelled) setData(buildCard(payload, archetypes, headline, salaries));
      } catch (e) {
        console.warn("Could not build the card from the sealed payload", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchMyReferralAssets(user.id).then((a) => setHandle(a?.handle ?? null));
  }, [user]);

  const cardData = useMemo<CareerCardData | null>(() => {
    if (!data) return null;
    return { ...data, handle };
  }, [data, handle]);

  const shareUrl = handle ? `https://wekit.ai/ref/${handle}` : "https://wekit.ai";
  const shareText = cardData?.personaTitle
    ? `I'm ${cardData.personaTitle}. I found my direction with WeKIT™ Career Clarity 360 — take yours:`
    : "Find your direction with WeKIT™ Career Clarity 360";

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const png = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.href = png;
      link.download = `wekit-career-card-${theme}.png`;
      link.click();
      toast.success("Card downloaded and ready to share.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't generate the image. Try a different theme.");
    } finally {
      setExporting(false);
    }
  };

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Career Identity", text: shareText, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("Copied — paste it anywhere.");
      } catch {
        toast.error("Couldn't copy. Try a share button instead.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            <WeKitLogo size="sm" />
          </Link>
          <Badge variant="secondary" className="gap-1 text-xs">
            <ImageIcon className="h-3 w-3 text-primary" /> Career Identity Card
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
            Your <span className="gradient-text">Career Identity</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Made to be shared. Pick a theme, download it, post it — every share
            helps another young person find their direction.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !cardData ? (
          <Card className="border-border/50 max-w-md mx-auto">
            <CardContent className="p-8 text-center space-y-4">
              <p className="text-muted-foreground">
                Your card is built from your report. Finish the assessment and it
                appears here.
              </p>
              <Button onClick={() => navigate("/start")} className="gap-2">
                <Sparkles className="h-4 w-4" /> Start assessment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Card preview */}
            <div className="lg:col-span-3 flex justify-center">
              <div
                className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-border/50"
                style={{
                  width: "100%",
                  maxWidth: 480,
                  aspectRatio: "1080 / 1350",
                }}
              >
                <div
                  style={{
                    width: 1080,
                    height: 1350,
                    transform: "scale(var(--card-scale))",
                    transformOrigin: "top left",
                  }}
                  className="origin-top-left"
                  ref={(el) => {
                    if (!el) return;
                    const parent = el.parentElement!;
                    const update = () => {
                      const w = parent.clientWidth;
                      el.style.setProperty("--card-scale", String(w / 1080));
                    };
                    update();
                    const ro = new ResizeObserver(update);
                    ro.observe(parent);
                  }}
                >
                  <CareerCard ref={cardRef} data={cardData} theme={theme} />
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="lg:col-span-2 space-y-5">
              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-5 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                      Theme
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {THEMES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setTheme(t.key)}
                          className={`flex items-center gap-3 rounded-xl border p-3 transition-all text-left ${
                            theme === t.key
                              ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                              : "border-border hover:border-primary/40"
                          }`}
                        >
                          <div className={`h-9 w-9 rounded-lg shrink-0 ${t.swatch}`} />
                          <span className="text-sm font-semibold text-foreground">
                            {t.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleDownload}
                    disabled={exporting}
                    className="w-full gap-2 h-11"
                  >
                    {exporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Download PNG
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-5 space-y-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Share
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <ShareTile href={shareLinks.whatsapp} icon={MessageCircle} label="WhatsApp" color="text-[#25D366]" />
                    <ShareTile href={shareLinks.linkedin} icon={Linkedin} label="LinkedIn" color="text-[#0A66C2]" />
                    <ShareTile href={shareLinks.twitter} icon={Twitter} label="X" color="text-foreground" />
                  </div>
                  <Button onClick={handleNativeShare} variant="outline" className="w-full gap-2">
                    <Share2 className="h-4 w-4" /> More options
                  </Button>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Tip: Instagram doesn't accept web shares — download the PNG
                    and upload it to your story.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Matches on this card come from the classified corpus, which draws on
            O*NET Web Services — licence condition, shown on every such page. */}
        <OnetAttribution className="mt-10" />
      </main>
    </div>
  );
}

function ShareTile({
  href,
  icon: Icon,
  label,
  color,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-all py-3"
    >
      <Icon className={`h-5 w-5 ${color}`} />
      <span className="text-xs font-medium text-foreground">{label}</span>
    </a>
  );
}
